/**
 * Text formatting utilities for accommodation data
 */

import React from "react";

/**
 * Converts newline characters (\r\n, \n, \r) to HTML line breaks
 * @param text - Text with newline characters
 * @returns Array of text segments separated by line breaks
 */
export const formatTextWithLineBreaks = (text: string | null | undefined): React.ReactNode[] => {
  if (!text) return [];

  // Split by various newline formats
  const lines = text.split(/\r\n|\r|\n/);

  return lines.flatMap((line, index) => {
    const isLast = index === lines.length - 1;
    return isLast ? [line] : [line, <br key={`br-${index}`} />];
  });
};

/**
 * Removes escape sequences from text
 * @param text - Text with escape sequences
 * @returns Cleaned text
 */
export const removeEscapeSequences = (text: string | null | undefined): string => {
  if (!text) return '';

  return text
    .replace(/\\r\\n/g, '\n')  // Convert literal \r\n to newline
    .replace(/\\n/g, '\n')     // Convert literal \n to newline
    .replace(/\\r/g, '\n')     // Convert literal \r to newline
    .replace(/\\t/g, ' ');     // Convert literal \t to space
};

/**
 * Decodes HTML entities in text
 * @param text - Text with HTML entities
 * @returns Decoded text
 */
export const decodeHTMLEntities = (text: string | null | undefined): string => {
  if (!text) return '';

  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

/**
 * Cleans and formats room/accommodation description text
 * - Decodes HTML entities (&amp;, &lt;, etc.)
 * - Removes escape sequences (\r\n, \n, etc.)
 * - Removes trailing backslashes
 * @param text - Raw description text
 * @returns Cleaned text
 */
export const cleanDescriptionText = (text: string | null | undefined): string => {
  if (!text) return '';

  // First decode HTML entities
  let cleaned = decodeHTMLEntities(text);

  // Then remove escape sequences
  cleaned = removeEscapeSequences(cleaned);

  // Remove trailing backslashes
  cleaned = cleaned.replace(/\\\s*$/gm, '');

  return cleaned.trim();
};
